import type { TPlayerBase } from 'video.js'
import type {
  AudioTrackList,
  Metadata,
  Player,
  Source,
  TextTrackList,
} from '../index.h'

import videojs from 'video.js'
import { SAFE_VOLUME, setCacheLastVolume } from '|>/shared/constants/volume'
import {
  addReceiverTracksToSenderTrackLists,
  clearTracks,
  getSelectedAudioTrack,
  getSelectedTextTrack,
  isSameNumbersArrays,
  SESSION_TIMEOUT,
} from '../lib'
import {
  ChromecastSessionManager,
  createSessionManager,
} from '../sessionManager'

const Tech = videojs.getComponent('Tech')

/**
 * Registers the ChromecastTech Tech with Video.js. Calls 'registerTech', which will add a Tech called
 * `chromecast` to the list of globally registered Video.js Tech implementations.
 *
 * Video.js Tech are initialized and used
 * automatically by Video.js Player instances. Whenever a new source is set on the player,
 * the player iterates through the list of available Tech to determine which to use to
 * play the source.
 */
export class ChromecastTech extends Tech {
  static canPlaySource: () => boolean
  static isSupported: () => boolean

  _media: chrome.cast.media.Media | undefined
  // media.activeTrackIds not updated immediately, so we need to cache the last requested active track ids
  _lastRequestedActiveTrackIds: { audio?: number; text?: number } = {}
  _lastRequestedActiveTrackIdsList: number[] | undefined
  _eventListeners: Array<{
    target: any
    type: string
    listener: (...args: any) => void
    callback: (...args: any) => void
    context: any
  }>
  _chromecastSessionManager: ChromecastSessionManager
  _hasPlayedAnyItem: boolean = false
  _hasMediaSessionEnded: boolean = false
  _remotePlayer: cast.framework.RemotePlayer | undefined
  _remotePlayerController: cast.framework.RemotePlayerController | undefined
  _currentSource: Source | null | undefined
  _initialStartTime: number
  _hasPlayedCurrentItem: boolean | undefined
  _isMediaLoading: boolean | undefined
  _requestTitle: (() => string) | undefined
  _requestSubtitle: (() => string) | undefined
  _requestCustomData: (() => any) | undefined
  _onTrackSetSuccess: (() => void) | undefined
  _onTrackSetError: (() => void) | undefined
  _onMediaInfoLoadError: ((reason: string) => void) | undefined
  _sessionTimeoutID: number | undefined
  _waitTimeoutID: number | undefined
  _audioTracks: AudioTrackList | undefined
  _textTracks: TextTrackList | undefined
  _isTracksSynced: boolean | undefined
  _isInitialTracksSet: boolean | undefined

  playerInst: Player

  constructor(options, ready) {
    super(options, ready)

    this._eventListeners = []
    this.playerInst = videojs(options.playerId)

    this._chromecastSessionManager =
      this.playerInst.chromecastSessionManager ??
      createSessionManager(this.playerInst)

    if (!ChromecastSessionManager.remotePlayerController) {
      this._chromecastSessionManager.createRemotePlayer()
    }

    this._remotePlayer = ChromecastSessionManager.remotePlayer
    this._remotePlayerController =
      ChromecastSessionManager.remotePlayerController

    this._listenToPlayerControllerEvents()
    this._listenToCastSessionState()

    this.on('dispose', () => {
      // sender player is disposed (f.e.closed by user)
      this._clearWaitTimeout()
      this._removeAllEventListeners()
      this._isTracksSynced = false
    })

    this._requestTitle = options.requestTitleFn
    this._requestSubtitle = options.requestSubtitleFn
    this._requestCustomData = options.requestCustomDataFn
    this._onTrackSetSuccess = options.onTrackSetSuccess
    this._onTrackSetError = options.onTrackSetError
    this._onMediaInfoLoadError = options.onMediaInfoLoadError

    this._initialStartTime =
      this.playerInst.currentTime() ||
      ChromecastSessionManager.prevTechState?.startTime ||
      options.startTime ||
      0

    // Tech.doReset calls resetCache_ which sets volume to 1, update it with real value asap
    setCacheLastVolume(this.playerInst as TPlayerBase)

    this._setReciverTracksToSender(
      this._getCurrentMedia(),
      this._getReceiverTracks() ?? []
    )

    this.triggerReady()

    this._waitForTitleAndPlay(options.source, this._initialStartTime)
  }

  _listenToCastSessionState(): void {
    const castContext = this._getCastContext?.()

    // Keep Tech state in sync even when the session is ended externally
    // (e.g. receiver stops casting, or Cast UI ends the session).
    if (castContext)
      this._addEventListener(
        castContext,
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        this._onCastSessionStateChanged,
        this
      )
  }

  _onCastSessionStateChanged(event: {
    sessionState?: cast.framework.SessionState
  }) {
    if (event?.sessionState === cast.framework.SessionState.SESSION_ENDED) {
      // Reset state that is only valid while casting. Tech instances can be created/disposed
      // without a corresponding session transition, so we handle this defensively here too.
      this._clearSessionTimeout()
      this._clearWaitTimeout()
      this._hasPlayedAnyItem = false
      this._hasMediaSessionEnded = false
      this._hasPlayedCurrentItem = false
      this._isMediaLoading = false
      this._isTracksSynced = false
      this._isInitialTracksSet = false
    }
  }

  /**
   * Waits up to 5 seconds (checking every 500ms) for title to be defined,
   * then calls _playSource with the provided source and start time.
   */
  _waitForTitleAndPlay(source: Source, startTime: number): void {
    const maxWaitTime = 5000
    const checkInterval = 500
    let elapsedTime = 0

    const checkAndPlay = () => {
      if (
        this._requestSubtitle?.() !== undefined &&
        this._requestTitle?.() !== undefined
      ) {
        this._playSource(source, startTime) // Title is defined
      } else if (elapsedTime < maxWaitTime) {
        elapsedTime += checkInterval // Still wait
        this._waitTimeoutID = setTimeout(
          checkAndPlay,
          checkInterval
        ) as unknown as number
      } else {
        this._playSource(source, startTime) // Timeout reached
      }
    }
    checkAndPlay()
  }

  _getSenderTracks(): {
    audioTracks: videojs.AudioTrackList
    textTracks: videojs.TextTrackList
  } {
    const audioTracks = this.playerInst.audioTracks()
    const textTracks = this.playerInst.textTracks()

    return { audioTracks, textTracks }
  }

  _getInitialSenderTracks() {
    const { initialAudio, initialText } =
      ChromecastSessionManager.prevTechState ?? {}

    return { initialAudio, initialText }
  }

  _getTracks() {
    if (!this._isTracksSynced)
      return { initialAudio: undefined, initialText: undefined }

    return this._getSenderTracks()
  }

  /**
   * Stores the given source and begins playback, starting at the beginning
   * of the media item.
   */
  setSource(source) {
    this._clearWaitTimeout()

    if (
      this._currentSource &&
      this._currentSource.src === source.src &&
      this._currentSource.type === source.type
    ) {
      // Skip setting the source if the `source` argument is the
      // same as what's already been set. This `setSource` function
      // calls `this._playSource` which sends a "load media" request
      // to the Chromecast PlayerController. Because this function
      // may be called multiple times in rapid succession with the same `source`
      // argument, we need to de-duplicate calls with the same `source` argument to
      // prevent overwhelming the Chromecast PlayerController with expensive "load
      // media" requests, which it itself does not de-duplicate.
      return
    }
    // We cannot use `this.playerInst.currentSource()` because the
    // value returned by that function is not the same as what's returned
    // by the Video.js Player's middleware after they are run. Also, simply
    // using `this.playerInst.src()` does not include mimetype information
    // which we pass to the Chromecast player.
    this._currentSource = source
    this._playSource(source, 0)
  }

  /**
   * Plays the given source, beginning at an optional starting time.
   */
  _playSource(source: Source | undefined, startTime: number) {
    const castSession = this._getCurrentCastSession()
    if (!castSession || !source) return

    this.playerInst.trigger('chromecastSource')

    const mediaInfo = new chrome.cast.media.MediaInfo(source.src, source.type)

    const customData = this._requestCustomData?.()
    mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata()
    mediaInfo.metadata.metadataType = chrome.cast.media.MetadataType.GENERIC
    this._updateTitles(mediaInfo.metadata)

    this._clearSessionTimeout()

    mediaInfo.streamType =
      this.playerInst.duration() === Infinity
        ? chrome.cast.media.StreamType.LIVE
        : chrome.cast.media.StreamType.BUFFERED

    castSession?.addEventListener(
      // called when media session is created, and removed.
      cast.framework.SessionEventType.MEDIA_SESSION,
      () => this._watchUserSelectedTrack()
    )

    if (customData) {
      // Custom data possible values could be checked there
      // https://github.com/bldsoft/stb-chromecast/tree/main
      mediaInfo.customData = customData
    }

    let request = new chrome.cast.media.LoadRequest(mediaInfo)
    request.autoplay = true
    request.currentTime = startTime || 0

    this._isMediaLoading = true
    this._hasPlayedCurrentItem = false
    this._isTracksSynced = false

    this.trigger('loadstart')
    castSession.loadMedia(request).catch((error: chrome.cast.ErrorCode) => {
      this._onMediaInfoLoadError?.(error)
      this._isMediaLoading = false
      this._triggerErrorEvent()
    })
  }

  _onMediaTracksAvailable(tracks: chrome.cast.media.Track[]): void {
    const media = this._getCurrentMedia()
    if (!media) return

    this._setReciverTracksToSender(media, tracks)
  }

  _updateTitles(metadata: Metadata) {
    const title = this._requestTitle?.() ?? ''
    const subtitle = this._requestSubtitle?.() ?? ''

    metadata.title = title
    metadata.subtitle = subtitle
  }

  _checkMediaInfoUpdates(): void {
    // check if tracks appear
    const receiverTracks = this._getReceiverTracks()
    if (receiverTracks?.length) this._onMediaTracksAvailable(receiverTracks)
  }

  /*
   * Set initial reciver tracks, based on sender tracks.
   * Should be set once per media.
   */
  _setInitialTracks(media: chrome.cast.media.Media | null): void {
    if (!media) return

    if (this._isInitialTracksSet) return // tracks should be set once per media
    this._isInitialTracksSet = true

    const { initialAudio, initialText } = this._getInitialSenderTracks() ?? {}

    const receiverTracks = this._getReceiverTracks() ?? []

    if (!receiverTracks?.length || (!initialAudio && !initialText)) return

    const targetAudioLang = initialAudio?.language
    let audioId: number | undefined // found in receiver tracks
    if (targetAudioLang) {
      audioId = receiverTracks.find(
        ({ type, language }) =>
          type === chrome.cast.media.TrackType.AUDIO &&
          (language.startsWith(targetAudioLang) ||
            targetAudioLang.startsWith(language))
      )?.trackId
    }

    const targetTextLang = initialText?.language
    let subtitleId: number | undefined // found in receiver tracks
    if (targetTextLang) {
      subtitleId = receiverTracks.find(
        ({ type, language }) =>
          type === chrome.cast.media.TrackType.TEXT &&
          (language.startsWith(targetTextLang) ||
            targetTextLang.startsWith(language))
      )?.trackId
    }

    if (!audioId && !subtitleId) return

    const ids: number[] = []

    if (typeof audioId === 'number') ids.push(audioId)
    if (typeof subtitleId === 'number') ids.push(subtitleId)

    this._setActiveTracks(media, ids)
  }

  _setReciverTracksToSender(
    media: chrome.cast.media.Media | null,
    tracks: chrome.cast.media.Track[]
  ): void {
    if (this._isTracksSynced) return

    if (!media || !tracks.length) return

    const { audioTracks, textTracks } = this._getSenderTracks()

    const { initialAudio, initialText } = this._getInitialSenderTracks() ?? {}

    // clear html5+vhs tracks to fill it with receiver player tracks
    if (audioTracks) clearTracks(audioTracks)
    if (textTracks) clearTracks(textTracks, this.playerInst.tech_)

    addReceiverTracksToSenderTrackLists({
      tracks,
      audioTracks,
      textTracks,
      initialAudioLanguage: initialAudio?.language,
      initialTextLanguage: initialText?.language,
      tech: this,
    })

    if (!this._isTracksSynced) {
      this._setInitialTracks(media)
      this._isTracksSynced = true
    }
  }

  /*
   * Set receiver active tracks.
   * ids - The track IDs to set as active. An empty array means "off" / default for all tracks.
   */
  _setActiveTracks(
    media: chrome.cast.media.Media | null,
    ids?: number[]
  ): void {
    if (!media || !ids) return

    // Avoid sending the same request repeatedly (common when track lists fire multiple changes).
    if (isSameNumbersArrays(ids, this._lastRequestedActiveTrackIdsList)) return

    const prevList = this._lastRequestedActiveTrackIdsList
    const prevObj = { ...this._lastRequestedActiveTrackIds }

    // Persist the most recent requested IDs (for comparisons + combine logic).
    this._lastRequestedActiveTrackIdsList = ids.slice()
    const tracks = this._getReceiverTracks() ?? []
    this._lastRequestedActiveTrackIds = {
      audio: tracks.find(
        (t) =>
          t.type === chrome.cast.media.TrackType.AUDIO &&
          ids.includes(t.trackId)
      )?.trackId,
      text: tracks.find(
        (t) =>
          t.type === chrome.cast.media.TrackType.TEXT && ids.includes(t.trackId)
      )?.trackId,
    }

    const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(ids)

    media.editTracksInfo(
      tracksInfoRequest,
      () => {
        this._onTrackSetSuccess?.()
      },
      () => {
        // Roll back cached "requested" ids if receiver rejected the change.
        this._lastRequestedActiveTrackIdsList = prevList
        this._lastRequestedActiveTrackIds = prevObj
        this._onTrackSetError?.()
      }
    )
  }

  _combineSelectedTracks(
    media: chrome.cast.media.Media | null,
    trackId: number | undefined,
    type: chrome.cast.media.TrackType
  ): number[] | undefined {
    if (!media || !type) return

    // When the user changes only audio (or only text), preserve the other type's
    // selection from the last requested IDs. `media.activeTrackIds` can lag behind.
    const alreadySelected = (media.activeTrackIds ?? []).slice()

    const cachedOther =
      type === chrome.cast.media.TrackType.AUDIO
        ? this._lastRequestedActiveTrackIds.text
        : this._lastRequestedActiveTrackIds.audio
    if (
      typeof cachedOther === 'number' &&
      !alreadySelected.includes(cachedOther)
    ) {
      alreadySelected.push(cachedOther)
    }

    if (!alreadySelected.length && trackId !== undefined) {
      return [trackId]
    }

    const idsOfType =
      this._getReceiverTracks()
        ?.filter((track) => track.type === type)
        .map((track) => track.trackId) ?? []

    // remove same type index
    const result = alreadySelected.filter((id) => !idsOfType.includes(id)) ?? []

    // add new index or nothing if trackId is undefined ('off' case)
    if (trackId !== undefined) result.push(trackId)

    return result
  }

  _watchUserSelectedTrack(): void {
    const { audioTracks, textTracks } = this._getSenderTracks()

    if (audioTracks)
      this._addEventListener(
        audioTracks,
        'change',
        () => {
          if (!this._isTracksSynced) return

          const selectedAudio = getSelectedAudioTrack(audioTracks)

          const media = this._getCurrentMedia()

          const tracksIds = this._combineSelectedTracks(
            media,
            selectedAudio?.id ?? undefined,
            chrome.cast.media.TrackType.AUDIO
          )

          this._setActiveTracks(media, tracksIds)
        },
        this
      )

    if (textTracks)
      this._addEventListener(
        textTracks,
        'change',
        () => {
          if (!this._isTracksSynced) return

          const selectedText = getSelectedTextTrack(textTracks)

          const media = this._getCurrentMedia()

          const tracksIds = this._combineSelectedTracks(
            media,
            selectedText?.id ?? undefined,
            chrome.cast.media.TrackType.TEXT
          )

          this._setActiveTracks(media, tracksIds)
        },
        this
      )
  }

  /**
   * Returns whether or not the current media item has finished playing.
   * Returns _hasMediaSessionEnded if media not exist.
   * or has not yet finished playing.
   */
  ended(value?: number): boolean {
    if (this._hasMediaSessionEnded) return true

    const currentTime = value ?? this.currentTime() ?? 0

    const duration = Math.floor(this.duration())

    const mediaSession = this._getCurrentMedia()

    if (
      (currentTime >= duration && duration > 0) ||
      mediaSession?.idleReason === chrome.cast.media.IdleReason.FINISHED ||
      (!mediaSession && this._hasMediaSessionEnded)
    )
      return true

    return false
  }

  updateEnded(value?: boolean): void {
    if (value === undefined) {
      const ended = this.ended()
      this._hasMediaSessionEnded = ended
      if (ended) this.trigger('ended')
    } else {
      this._hasMediaSessionEnded = value
      if (value) this.trigger('ended')
    }
  }

  /**
   * Manually updates the current time. The playback position will jump to
   * the given time and continue playing if the item was playing when `setCurrentTime`
   * was called, or remain paused if the item was paused.
   */
  setCurrentTime(time) {
    const duration = this.duration()
    const remotePlayer = this._remotePlayer
    const remotePlayerController = this._remotePlayerController

    if (time > duration || !remotePlayer?.canSeek || !remotePlayerController) {
      return
    }
    // Seeking to any place within (approximately) 1 second of the end of the item
    // causes the Video.js player to get stuck in a BUFFERING state. To work around
    // this, we only allow seeking to within 1 second of the end of an item.
    remotePlayer.currentTime = Math.min(duration - 1, time)
    remotePlayerController.seek()

    // instantly update time - more resonsive UI updates on receiver.
    this._triggerTimeUpdateEvent({ value: time })
  }

  setSenderVolume() {
    const { volume, muted, isMobile } =
      ChromecastSessionManager.prevTechState ?? {}
    const targetVolume = isMobile ? SAFE_VOLUME : volume

    this.setMuted(muted)
    this.setVolume(targetVolume)
  }

  // ****************** API *******************

  /**
   * Resumes playback if a media item is paused or restarts an item from
   * its beginning if the item has played and ended.
   */
  play() {
    if (!this.paused()) {
      return
    }
    if (this.ended() && !this._isMediaLoading) {
      // Restart the current item from the beginning
      this._playSource(this.playerInst.currentSource() as unknown as Source, 0)
    } else {
      this._remotePlayerController?.playOrPause()
    }
  }

  /**
   * Pauses playback if the player is not already paused and if the current media item
   * has not ended yet.
   */
  pause() {
    if (!this.paused() && this._remotePlayer?.canPause) {
      this._remotePlayerController?.playOrPause()
    }
  }

  /**
   * Returns whether or not the player is "paused". Video.js'
   * definition of "paused" is "playback paused" OR "not playing".
   */
  paused() {
    return (
      this._remotePlayer?.isPaused ||
      this.ended() ||
      this._remotePlayer?.playerState === null
    )
  }

  /**
   * Returns the current playback time position.
   */
  currentTime() {
    if (!this._hasPlayedAnyItem) {
      return this._initialStartTime
    }
    return this._remotePlayer?.currentTime
  }

  /**
   * Returns the duration of the current media item, or `0` if the source
   * is not set or if the duration of the item is not available from the
   * Chromecast API yet.
   */
  duration() {
    if (!this._hasPlayedAnyItem) {
      return this.playerInst.duration() ?? 0
    }
    return this._remotePlayer?.duration ?? 0
  }

  /**
   * Returns the current volume level setting as a decimal number between `0` and `1`.
   */
  volume() {
    return this._remotePlayer?.volumeLevel
  }

  /**
   * Sets the current volume level. Volume level is a decimal number
   * between `0` and `1`, where `0` is muted and `1` is the loudest volume level.
   */
  setVolume(volumeLevel) {
    if (this._remotePlayer) this._remotePlayer.volumeLevel = volumeLevel

    this._remotePlayerController?.setVolumeLevel() // Sets the volume level of receiver device same as remotePlayer value

    this.trigger('volumechange')
  }

  /**
   * Returns whether or not the player is currently muted.
   */
  muted() {
    return this._remotePlayer?.isMuted
  }

  /**
   * Mutes or un-mutes the player. Does nothing if the player is currently
   * muted and the `isMuted` parameter is true or if the player is not muted and
   * `isMuted` is false.
   */
  setMuted(isMuted) {
    if (
      (this._remotePlayer?.isMuted && !isMuted) ||
      (!this._remotePlayer?.isMuted && isMuted)
    ) {
      this._remotePlayerController?.muteOrUnmute()
    }
  }

  poster() {
    // not supported
  }

  setPoster() {
    // not supported
  }

  buffered() {
    return undefined
  }

  seekable() {
    return videojs.createTimeRange(0, this.duration())
  }

  seeking() {
    // TODO need investigate
    return false
  }

  /**
   * Not applicable to this Tech.
   */
  controls() {
    return false
  }

  /**
   * Returns whether or not the browser should show the player
   * "inline" (non-fullscreen) by default. This function always
   * returns true to tell the browser that non-fullscreen playback is preferred.
   */
  playsinline() {
    return true
  }

  /**
   * Returns whether or not fullscreen is supported by this Tech.
   * Always returns `true` because fullscreen is always supported.
   */
  supportsFullScreen() {
    return true
  }

  /**
   * Sets a flag that determines whether or not the media should automatically begin
   * playing on page load. This is not supported because a Chromecast session must be
   * initiated by casting via the casting menu and cannot autoplay.
   */
  setAutoplay() {
    // Not supported
  }

  /**
   * @returns {number} the chromecast player's playback rate, if available. Otherwise,
   * the return value defaults to `1`.
   */
  playbackRate() {
    const mediaSession = this._getCurrentMedia()

    return mediaSession ? mediaSession.playbackRate : 1
  }

  /**
   * Does nothing. Changing the playback rate is not supported.
   */
  setPlaybackRate() {
    // Not supported
  }

  /**
   * Does nothing. Satisfies calls to the missing preload method.
   */
  preload() {
    // Not supported
  }

  /**
   * Causes the Tech to begin loading the current source. `load`
   * is not supported in this ChromecastTech because setting the
   * source on the `Chromecast` automatically causes it to begin loading.
   */
  load() {
    // Not supported
  }

  /**
   * Gets the Chromecast equivalent of HTML5 Media Element's `readyState`.
   */
  readyState() {
    if (
      this._remotePlayer?.playerState === 'IDLE' ||
      this._remotePlayer?.playerState === 'BUFFERING'
    ) {
      return 0 // HAVE_NOTHING
    }
    return 4
  }
  // ****************** API *******************

  // ****************** Remote Controller events *******************

  _onPlayerCanPauseChanged() {
    if (this._remotePlayer?.canPause) {
      if (ChromecastSessionManager.prevTechState?.paused) {
        this.pause()
      }
    }
  }

  _onPlayerPausedChanged() {
    if (this._remotePlayer?.isPaused) {
      this.trigger('pause')
    } else {
      this.trigger('play')
      this.trigger('playing')
    }
  }

  _onConnectedChanged() {
    if (!this._remotePlayer?.isConnected) {
      // logger('Chromecast Device not connected anymore')
    }
  }

  _onRemoteVolumeEvent() {
    if (!this._hasPlayedAnyItem) {
      this.setSenderVolume()
    } else {
      this.trigger('volumechange')
    }
  }

  _onRemoteMuteEvent() {
    if (!this._hasPlayedAnyItem) {
      this.setSenderVolume()
    } else {
      this.trigger('volumechange')
    }
  }

  _onMediaInfoChanged() {
    // tracks are part of media info, so we need to check if they are changed
    this._checkMediaInfoUpdates()
  }

  _triggerTimeUpdateEvent({ value }) {
    this.trigger('timeupdate')
    this.updateEnded(this.ended(value))
  }

  _triggerDurationChangeEvent() {
    this.trigger('durationchange')
  }

  _triggerErrorEvent() {
    this.trigger('error')
  }

  /**
   * Wires up event listeners for RemotePlayerController events.
   */
  _listenToPlayerControllerEvents() {
    const eventTypes = cast.framework.RemotePlayerEventType

    this._addEventListener(
      this._remotePlayerController,
      eventTypes.CAN_PAUSE_CHANGED,
      this._onPlayerCanPauseChanged,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.IS_PAUSED_CHANGED,
      this._onPlayerPausedChanged,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.PLAYER_STATE_CHANGED,
      this._onPlayerStateChanged,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.VOLUME_LEVEL_CHANGED,
      this._onRemoteVolumeEvent,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.IS_MUTED_CHANGED,
      this._onRemoteMuteEvent,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.CURRENT_TIME_CHANGED,
      this._triggerTimeUpdateEvent,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.DURATION_CHANGED,
      this._triggerDurationChangeEvent,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.IS_MEDIA_LOADED_CHANGED,
      this._onMediaLoadedChanged,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.IS_CONNECTED_CHANGED,
      this._onConnectedChanged,
      this
    )
    this._addEventListener(
      this._remotePlayerController,
      eventTypes.MEDIA_INFO_CHANGED,
      this._onMediaInfoChanged,
      this
    )
  }

  _onMediaLoadedChanged() {
    const isMediaLoaded = this._remotePlayer?.isMediaLoaded

    if (isMediaLoaded) {
      this._clearSessionTimeout()

      const castSession = this._getCurrentCastSession()
      if (!castSession) return

      // Set up media session listener
      const mediaSession = castSession.getMediaSession()

      if (mediaSession) {
        mediaSession.addUpdateListener(
          this._onMediaSessionStatusChanged.bind(this)
        )
      }

      this.trigger('loadeddata')
      this._isMediaLoading = false

      if (!this._hasPlayedAnyItem) this._hasPlayedAnyItem = true

      this.setSenderVolume()
    }
  }

  /**
   * Registers an event listener on the given target object.
   * Because many objects in the Chromecast API are either singletons
   * or must be shared between instances of `ChromecastTech` for the
   * lifetime of the player, we must unbind the listeners when this Tech
   * instance is destroyed to prevent memory leaks. To do that, we need to keep
   * a reference to listeners that are added to global objects so that we can
   * use those references to remove the listener when this Tech is destroyed.
   */
  _addEventListener(target, type, callback, context) {
    const listener = {
      target: target,
      type: type,
      callback: callback,
      context: context,
      listener: callback.bind(context),
    }
    target.addEventListener(type, listener.listener)
    this._eventListeners.push(listener)
  }

  /**
   * Removes all event listeners that were registered with global objects during the
   * lifetime of this Tech. See {@link _addEventListener} for more information
   * about why this is necessary.
   */
  _removeAllEventListeners() {
    while (this._eventListeners.length > 0) {
      this._removeEventListener(this._eventListeners[0])
    }
    this._eventListeners = []
  }

  /**
   * Removes a single event listener that was registered with global objects
   * during the lifetime of this Tech. See {@link _addEventListener} for
   * more information about why this is necessary.
   */
  _removeEventListener(listener) {
    let index = -1
    let pass = false
    let i

    listener.target.removeEventListener(listener.type, listener.listener)

    for (i = 0; i < this._eventListeners.length; i++) {
      pass =
        this._eventListeners[i].target === listener.target &&
        this._eventListeners[i].type === listener.type &&
        this._eventListeners[i].callback === listener.callback &&
        this._eventListeners[i].context === listener.context

      if (pass) {
        index = i
        break
      }
    }

    if (index !== -1) {
      this._eventListeners.splice(index, 1)
    }
  }

  /**
   * Handles Chromecast player state change events. The player may "change state" when
   * paused, played, buffering, etc.
   */
  _onPlayerStateChanged() {
    if (!this._remotePlayer) return

    const states = chrome.cast.media.PlayerState,
      playerState = this._remotePlayer.playerState

    if (playerState === states.PLAYING) {
      this._hasPlayedCurrentItem = true
      this.trigger('play')
      this.trigger('playing')
    } else if (playerState === states.PAUSED) {
      this.trigger('pause')
    } else if (
      (playerState === states.IDLE && this.ended()) ||
      (playerState === null && this._hasPlayedCurrentItem)
    ) {
      this._hasPlayedCurrentItem = false
      this._closeSessionOnTimeout()
      this.trigger('ended')
    } else if (playerState === states.BUFFERING) {
      this.trigger('waiting')
    }
  }

  /**
   * Handles Chromecast MediaSession state change events. The only property sent
   * to this event is whether the session is alive. This is useful for determining
   * if an item has ended as the MediaSession will fire this event with `false` then
   * be immediately destroyed. This means that we cannot trust `idleReason` to show
   * whether an item has ended since we may no longer have access to the MediaSession.
   */
  _onMediaSessionStatusChanged() {
    const media = this._getCurrentMedia()

    if (!media) {
      this.updateEnded()
    }

    if (media) {
      // Handle media status changes
      switch (media.playerState) {
        case chrome.cast.media.PlayerState.PLAYING:
          this.trigger('playing')
          break
        case chrome.cast.media.PlayerState.PAUSED:
          this.trigger('pause')
          break
        case chrome.cast.media.PlayerState.IDLE:
          if (media.idleReason === chrome.cast.media.IdleReason.FINISHED) {
            this.trigger('ended')
          }
          break
        case chrome.cast.media.PlayerState.BUFFERING:
          this.trigger('waiting')
          break
      }
    }
  }

  /**
   * Ends the session after a certain number of seconds of inactivity.
   *
   * If the Chromecast player is in the "IDLE" state after an item has ended, and no
   * further items are queued up to play, the session is considered inactive. Once a
   * period of time (currently 10 seconds) has elapsed with no activity, we manually
   * end the session to prevent long periods of a blank Chromecast screen that is
   * shown at the end of item playback.
   */
  _closeSessionOnTimeout() {
    // Ensure that there's never more than one session timeout active
    this._clearSessionTimeout()
    this._sessionTimeoutID = setTimeout(() => {
      const castSession = this._getCurrentCastSession()

      if (castSession) {
        castSession.endSession(true)
      }
      this._clearSessionTimeout()
    }, SESSION_TIMEOUT) as unknown as number
  }

  /**
   * Stops the timeout that is waiting during a period of inactivity in order to close
   * the session.
   */
  _clearSessionTimeout() {
    if (this._sessionTimeoutID) {
      clearTimeout(this._sessionTimeoutID)
      this._sessionTimeoutID = undefined
    }
  }

  /**
   * Stops the timeout that is waiting for the title.
   */
  _clearWaitTimeout() {
    if (this._waitTimeoutID) {
      clearTimeout(this._waitTimeoutID)
      this._waitTimeoutID = undefined
    }
  }

  _getReceiverTracks(): chrome.cast.media.Track[] | null | undefined {
    return this._getCurrentMedia()?.media?.tracks
  }

  _getCurrentMetadata(): Metadata | undefined {
    return this._getCurrentMediaInfo()?.metadata
  }

  _getCurrentMediaInfo(): chrome.cast.media.MediaInfo | null {
    return this._getCurrentMedia()?.media ?? null
  }

  _getCurrentMedia(): chrome.cast.media.Media | null {
    const castSession = this._getCurrentCastSession()
    return castSession ? castSession.getMediaSession() : null
  }

  _getCurrentCastSession(): cast.framework.CastSession | null {
    return this._getCastContext().getCurrentSession()
  }

  _getCastState() {
    return this._getCurrentCastSession()?.getSessionState()
  }

  _getCastContext() {
    return this._chromecastSessionManager.getCastContext()
  }
}

ChromecastTech.canPlaySource = (): boolean => {
  return ChromecastSessionManager.isChromecastConnected()
}

ChromecastTech.isSupported = (): boolean => {
  return (
    ChromecastSessionManager.isChromecastSupported() &&
    ChromecastSessionManager.isChromecastConnected()
  )
}
